import {
  IconChecklist,
  IconClipboardCheck,
  IconContract,
  IconFileInvoice,
  IconHistory,
  IconPlus,
  IconReceiptTax,
  IconReportAnalytics,
  IconSettings,
  IconShoppingCart,
} from "@tabler/icons-react";
import { tieneRolAutorizador } from "@/lib/roles";
import { PERMISOS, PERMISOS_ADMIN, VER_SOLICITUDES } from "@/lib/permisos";

/**
 * Catálogo declarativo de acciones rápidas del dashboard. Cada entrada
 * declara los PERMISOS que la habilitan (RBAC · Ruta B); el container del
 * dashboard filtra la lista contra los permisos del usuario actual.
 *
 * `tituloMesaDeControlSola` es una regla de ETIQUETA (no de acceso) · cuando el
 * usuario es Mesa de Control pero NO Autorizador, mostramos un título distinto
 * para la bandeja ("Mesa de Control" en vez de "Bandeja de autorización").
 */
export interface QuickActionDef {
  id: string;
  icon: typeof IconPlus;
  title: string;
  description: string;
  to: string;
  /** Search params opcionales (ej. `?tipo=ca` para preseleccionar el tipo). */
  search?: Record<string, string>;
  permisosQueLaVen: ReadonlyArray<string>;
  accent?: boolean;
  /** Etiqueta del botón CTA a la derecha de la fila · si se omite, el botón
   *  muestra el título de la acción. Las 3 acciones de creación (FB60/CA/OC)
   *  la fijan a "Nueva solicitud" para que el botón sea uniforme mientras el
   *  título distingue el tipo. */
  cta?: string;
  /** Si está presente, sobrescribe `title` cuando el usuario cae en este caso. */
  tituloMesaDeControlSola?: string;
}

export const QUICK_ACTIONS: ReadonlyArray<QuickActionDef> = [
  {
    id: "nueva-solicitud",
    icon: IconReceiptTax,
    title: "FB60",
    description: "Factura con CFDI del SAT · CFDI único o múltiples",
    to: "/solicitudes/nueva",
    search: { tipo: "fb60" },
    permisosQueLaVen: [PERMISOS.solicitudesGestionar],
    accent: true,
    cta: "Nueva solicitud",
  },
  {
    id: "nueva-solicitud-ca",
    icon: IconContract,
    title: "Contrato Anualizado",
    description: "Pago contra un contrato anualizado de SAP",
    to: "/solicitudes/nueva",
    search: { tipo: "ca" },
    permisosQueLaVen: [PERMISOS.solicitudesGestionar],
    accent: true,
    cta: "Nueva solicitud",
  },
  {
    id: "nueva-solicitud-oc",
    icon: IconShoppingCart,
    title: "Orden de Compra",
    description: "Pago contra una orden de compra de SAP",
    to: "/solicitudes/nueva",
    search: { tipo: "oc" },
    permisosQueLaVen: [PERMISOS.solicitudesGestionar],
    accent: true,
    cta: "Nueva solicitud",
  },
  {
    id: "mis-solicitudes",
    icon: IconFileInvoice,
    title: "Mis solicitudes de pago",
    description: "Consulta su estado y continúa las pendientes",
    to: "/solicitudes",
    permisosQueLaVen: VER_SOLICITUDES,
    cta: "Ir",
  },
  {
    id: "bandeja-autorizacion",
    icon: IconChecklist,
    title: "Bandeja de autorización",
    tituloMesaDeControlSola: "Mesa de Control",
    description: "Solicitudes pendientes de autorizar",
    to: "/autorizacion/bandeja",
    permisosQueLaVen: [PERMISOS.autorizacionAutorizar],
  },
  {
    id: "historial-autorizaciones",
    icon: IconClipboardCheck,
    title: "Historial de autorizaciones",
    description: "Niveles que ya he autorizado",
    to: "/autorizacion/historial",
    permisosQueLaVen: [PERMISOS.autorizacionAutorizar],
  },
  {
    id: "reporteria",
    icon: IconReportAnalytics,
    title: "Reportería",
    description: "Arma y exporta reportes dinámicos",
    to: "/reporteria",
    permisosQueLaVen: [PERMISOS.reporteriaUsar],
    cta: "Abrir",
  },
  {
    id: "admin",
    icon: IconSettings,
    title: "Panel de administración",
    description: "Catálogos · usuarios · mock SAT",
    to: "/admin",
    permisosQueLaVen: PERMISOS_ADMIN,
  },
  {
    id: "auditoria",
    icon: IconHistory,
    title: "Auditoría",
    description: "Operaciones · accesos · cambios admin",
    to: "/admin/auditoria",
    permisosQueLaVen: [PERMISOS.auditoriaVer],
  },
];

/**
 * Filtra el catálogo a las acciones disponibles para el set de PERMISOS del
 * usuario, aplicando la regla de ETIQUETA "Mesa de Control sola" (que depende
 * del ROL, no del permiso) cuando aplica.
 */
export function accionesDisponiblesPara(
  permisos: ReadonlyArray<string>,
  roles: ReadonlyArray<string>
): {
  action: QuickActionDef;
  titulo: string;
}[] {
  const setPermisos = new Set(permisos);
  const setRoles = new Set(roles);
  return QUICK_ACTIONS.filter((a) =>
    a.permisosQueLaVen.some((p) => setPermisos.has(p))
  ).map((a) => ({
    action: a,
    titulo:
      a.tituloMesaDeControlSola && setRoles.has("MesaDeControl") && !tieneRolAutorizador(roles)
        ? a.tituloMesaDeControlSola
        : a.title,
  }));
}
